import {
  acceptInvitation as acceptInvitationRoute,
  type InvitationResolution,
  type InvitationStatus,
  listMyOrganizations,
  type OrganizationMembership,
  resolveInvitation as resolveInvitationRoute,
} from "@fleetminder/contracts";

import { callApi, toWebApiError, WebApiError } from "./api-client.js";
import { loadBrowserEnvironment } from "./runtime-env.js";

export type SignUpRequest = {
  readonly companyName: string;
  readonly email: string;
  readonly name: string;
  readonly password: string;
  readonly returnTo?: string | null;
};

export type SignInRequest = {
  readonly email: string;
  readonly password: string;
  readonly rememberMe: boolean;
  readonly returnTo?: string | null;
};

export type SignUpResult =
  | {
      readonly kind: "organization_onboarding";
      readonly organizationOnboardingPath: string;
    }
  | {
      readonly kind: "verification_pending";
      readonly verificationPendingPath: string;
    }
  | {
      readonly kind: "failed";
      readonly message: string;
    };

export type SignInResult =
  | {
      readonly destination: string;
      readonly kind: "signed_in";
    }
  | {
      readonly kind: "verification_pending";
      readonly verificationPendingPath: string;
    }
  | {
      readonly kind: "account_disabled";
      readonly message: string;
      readonly supportPath: string;
    }
  | {
      readonly kind: "failed";
      readonly message: string;
    };

export type GoogleSignInResult =
  | {
      readonly authorizationUrl: string;
      readonly kind: "redirect";
    }
  | {
      readonly kind: "failed";
      readonly message: string;
    };

export type AuthProviders = {
  readonly google: boolean;
};

export type PasswordRecoveryRequestResult =
  | { readonly kind: "requested" }
  | { readonly kind: "failed"; readonly message: string };

export type PasswordResetResult =
  | { readonly kind: "reset" }
  | { readonly kind: "invalid_token"; readonly message: string }
  | { readonly kind: "failed"; readonly message: string };

export type InvitationAcceptanceResult =
  | { readonly kind: "accepted"; readonly organizationId: string }
  | { readonly kind: "sign_in_required" }
  | {
      readonly code: string;
      readonly kind: "failed";
      readonly message: string;
    };

const pendingOrganizationStorageKey = "fleetminder:pending-organization";

export async function signUpWithEmail(
  request: SignUpRequest,
  fetcher: typeof fetch = fetch,
  location: Pick<Location, "origin"> = window.location,
): Promise<SignUpResult> {
  const env = loadBrowserEnvironment();
  const response = await fetcher(
    new URL("/api/v1/auth/sign-up/email", env.VITE_API_BASE_URL),
    {
      body: JSON.stringify({
        callbackURL: createVerificationCallbackUrl(
          location.origin,
          isInvitationPath(request.returnTo)
            ? getSafeVerificationDestination(request.returnTo)
            : "/app/onboarding/organization",
        ),
        email: request.email,
        name: request.name,
        password: request.password,
        rememberMe: true,
      }),
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
    },
  );

  if (!response.ok) {
    return {
      kind: "failed",
      message: getProviderSafeSignUpFailureMessage(response.status),
    };
  }

  const body = (await response.json().catch(() => undefined)) as
    | {
        readonly user?: {
          readonly emailVerified?: boolean;
        };
      }
    | undefined;
  const organizationOnboardingPath = getSafeVerificationDestination(
    request.returnTo,
  );

  // Someone joining through an invitation is not setting up a company.
  if (!isInvitationPath(request.returnTo))
    savePendingOrganization({
      companyName: request.companyName,
      email: request.email,
    });

  if (body?.user?.emailVerified !== true) {
    return {
      kind: "verification_pending",
      verificationPendingPath: createVerificationPendingPath({
        destination: organizationOnboardingPath,
        email: request.email,
      }),
    };
  }

  return {
    kind: "organization_onboarding",
    organizationOnboardingPath,
  };
}

export async function requestVerificationEmail(
  input: {
    readonly destination: string;
    readonly email: string;
  },
  fetcher: typeof fetch = fetch,
  location: Pick<Location, "origin"> = window.location,
): Promise<SignUpResult> {
  const env = loadBrowserEnvironment();
  const response = await fetcher(
    new URL("/api/v1/auth/send-verification-email", env.VITE_API_BASE_URL),
    {
      body: JSON.stringify({
        callbackURL: createVerificationCallbackUrl(
          location.origin,
          input.destination,
        ),
        email: input.email,
      }),
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
    },
  );

  if (!response.ok) {
    return {
      kind: "failed",
      message: getProviderSafeVerificationFailureMessage(response.status),
    };
  }

  return {
    kind: "verification_pending",
    verificationPendingPath: createVerificationPendingPath(input),
  };
}

export async function signInWithEmail(
  request: SignInRequest,
  fetcher: typeof fetch = fetch,
  location: Pick<Location, "origin"> = window.location,
): Promise<SignInResult> {
  const env = loadBrowserEnvironment();
  const returnTo = getSafeReturnPath(request.returnTo);
  const response = await fetcher(
    new URL("/api/v1/auth/sign-in/email", env.VITE_API_BASE_URL),
    {
      body: JSON.stringify({
        callbackURL: createVerificationCallbackUrl(location.origin, returnTo),
        email: request.email,
        password: request.password,
        rememberMe: request.rememberMe,
      }),
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
    },
  );

  if (!response.ok) {
    if (response.status === 403) {
      return {
        kind: "verification_pending",
        verificationPendingPath: createVerificationPendingPath({
          destination: returnTo,
          email: request.email,
        }),
      };
    }

    return {
      kind: "failed",
      message: getProviderSafeSignInFailureMessage(response.status),
    };
  }

  let organizations: readonly OrganizationMembership[];

  try {
    organizations = await fetchMemberships(fetcher);
  } catch (error) {
    if (!(error instanceof WebApiError)) {
      throw error;
    }

    if (error.code === "account_disabled") {
      await signOut(fetcher);

      return {
        kind: "account_disabled",
        message:
          "Your account is turned off, so you can't sign in right now. Email us and we'll help.",
        supportPath: "mailto:support@fleetminder.app",
      };
    }

    if (error.code === "email_verification_required") {
      return {
        kind: "verification_pending",
        verificationPendingPath: createVerificationPendingPath({
          destination: returnTo,
          email: request.email,
        }),
      };
    }

    return {
      kind: "failed",
      message: getProviderSafeSignInFailureMessage(error.status),
    };
  }

  return {
    destination: getPostSignInDestination(organizations, returnTo),
    kind: "signed_in",
  };
}

/** Starts an OAuth handoff without following the provider redirect as fetch. */
export async function startGoogleSignIn(
  input: {
    readonly returnTo?: string | null;
    readonly signUp?: boolean;
  },
  fetcher: typeof fetch = fetch,
  location: Pick<Location, "origin"> = window.location,
): Promise<GoogleSignInResult> {
  const env = loadBrowserEnvironment();
  const returnTo = getSafeReturnPath(input.returnTo);
  const joining = isInvitationPath(returnTo);
  const callbackPath =
    input.signUp && !joining ? "/app/onboarding/organization" : returnTo;
  // A new account made to accept an invitation goes back to it, not to
  // setting up a company.
  const newUserPath = joining ? returnTo : "/app/onboarding/organization";
  const errorPath = input.signUp ? "/auth/sign-up" : "/auth/sign-in";
  const response = await fetcher(
    new URL("/api/v1/auth/sign-in/social", env.VITE_API_BASE_URL),
    {
      body: JSON.stringify({
        callbackURL: new URL(callbackPath, location.origin).toString(),
        disableRedirect: true,
        errorCallbackURL: new URL(
          `${errorPath}?oauth=failed`,
          location.origin,
        ).toString(),
        newUserCallbackURL: new URL(newUserPath, location.origin).toString(),
        provider: "google",
      }),
      credentials: "include",
      headers: { "content-type": "application/json" },
      method: "POST",
    },
  );

  if (!response.ok) {
    return { kind: "failed", message: getGoogleSignInFailureMessage() };
  }

  const body = (await response.json().catch(() => undefined)) as
    | { readonly url?: unknown }
    | undefined;

  if (typeof body?.url !== "string" || !body.url.startsWith("https://")) {
    return { kind: "failed", message: getGoogleSignInFailureMessage() };
  }

  return { authorizationUrl: body.url, kind: "redirect" };
}

export async function fetchAuthProviders(
  fetcher: typeof fetch = fetch,
): Promise<AuthProviders> {
  const env = loadBrowserEnvironment();

  try {
    const response = await fetcher(
      new URL("/api/v1/auth/providers", env.VITE_API_BASE_URL),
      { credentials: "include" },
    );
    const body = (await response.json().catch(() => undefined)) as
      | { readonly google?: unknown }
      | undefined;

    return { google: response.ok && body?.google === true };
  } catch {
    return { google: false };
  }
}

export async function startGoogleAccountLink(
  fetcher: typeof fetch = fetch,
  location: Pick<Location, "origin"> = window.location,
): Promise<GoogleSignInResult> {
  const env = loadBrowserEnvironment();
  const response = await fetcher(
    new URL("/api/v1/auth/link-social", env.VITE_API_BASE_URL),
    {
      body: JSON.stringify({
        callbackURL: new URL("/app/account", location.origin).toString(),
        disableRedirect: true,
        errorCallbackURL: new URL(
          "/app/account?identity=failed",
          location.origin,
        ).toString(),
        provider: "google",
      }),
      credentials: "include",
      headers: { "content-type": "application/json" },
      method: "POST",
    },
  );

  if (!response.ok) {
    return { kind: "failed", message: getGoogleAccountLinkFailureMessage() };
  }

  const body = (await response.json().catch(() => undefined)) as
    | { readonly url?: unknown }
    | undefined;

  return typeof body?.url === "string" && body.url.startsWith("https://")
    ? { authorizationUrl: body.url, kind: "redirect" }
    : { kind: "failed", message: getGoogleAccountLinkFailureMessage() };
}

/**
 * Memberships for the signed-in caller. Credentials go only to the configured
 * first-party API origin, never to an origin taken from a link or response.
 */
export async function fetchOrganizations(
  fetcher: typeof fetch = fetch,
): Promise<readonly OrganizationMembership[]> {
  try {
    return await fetchMemberships(fetcher);
  } catch (error) {
    if (error instanceof WebApiError) {
      return [];
    }

    throw error;
  }
}

/**
 * A success whose body does not parse is read as no memberships, as it
 * always has been: the session is real, and landing on onboarding is
 * recoverable where failing sign-in outright is not.
 */
async function fetchMemberships(
  fetcher: typeof fetch,
): Promise<readonly OrganizationMembership[]> {
  try {
    const { organizations } = await callApi(listMyOrganizations, {}, fetcher);

    return organizations;
  } catch (error) {
    if (error instanceof SyntaxError) {
      return [];
    }

    throw error;
  }
}

/**
 * Changes the password through Better Auth. The current password is the
 * reauthentication step: possession of a signed-in browser is not enough.
 */
export async function changePassword(
  input: {
    readonly currentPassword: string;
    readonly newPassword: string;
    readonly revokeOtherSessions: boolean;
  },
  fetcher: typeof fetch = fetch,
): Promise<void> {
  const env = loadBrowserEnvironment();
  const response = await fetcher(
    new URL("/api/v1/auth/change-password", env.VITE_API_BASE_URL),
    {
      body: JSON.stringify(input),
      credentials: "include",
      headers: { "content-type": "application/json" },
      method: "POST",
    },
  );

  if (!response.ok) {
    throw await toWebApiError(response);
  }
}

/** Ends every session except the one making the request. */
export async function revokeOtherSessions(
  fetcher: typeof fetch = fetch,
): Promise<void> {
  const env = loadBrowserEnvironment();
  const response = await fetcher(
    new URL("/api/v1/auth/revoke-other-sessions", env.VITE_API_BASE_URL),
    {
      credentials: "include",
      headers: { "content-type": "application/json" },
      method: "POST",
    },
  );

  if (!response.ok) {
    throw await toWebApiError(response);
  }
}

export async function signOut(fetcher: typeof fetch = fetch): Promise<void> {
  const env = loadBrowserEnvironment();

  await fetcher(new URL("/api/v1/auth/sign-out", env.VITE_API_BASE_URL), {
    credentials: "include",
    method: "POST",
  });
}

export async function resolveInvitation(
  token: string,
  fetcher: typeof fetch = fetch,
): Promise<InvitationResolution | undefined> {
  try {
    return await callApi(resolveInvitationRoute, { body: { token } }, fetcher);
  } catch (error) {
    // Any failure, including a body that will not parse, reads as an
    // invitation that cannot be shown; the page says so rather than crashing.
    if (error instanceof WebApiError || error instanceof SyntaxError) {
      return undefined;
    }

    throw error;
  }
}

export async function acceptInvitation(
  token: string,
  fetcher: typeof fetch = fetch,
): Promise<InvitationAcceptanceResult> {
  try {
    const { organizationId } = await callApi(
      acceptInvitationRoute,
      { body: { token } },
      fetcher,
    );

    return { kind: "accepted", organizationId };
  } catch (error) {
    if (!(error instanceof WebApiError)) {
      throw error;
    }

    if (error.status === 401) {
      return { kind: "sign_in_required" };
    }

    return {
      code: error.code,
      kind: "failed",
      // An envelope without a code arrives as the generic "request_failed",
      // which reads the same as an invitation that was not found.
      message: getInvitationStatusMessage(error.code),
    };
  }
}

/**
 * An invitation link as the sign-in and sign-up pages carry it, so a person
 * who has to create an account first comes back to the invitation rather
 * than to setting up a company of their own.
 */
export function isInvitationPath(path: string | null | undefined): boolean {
  return Boolean(path?.startsWith("/invitations/accept?"));
}

const alreadyJoined =
  "You've already joined this company. Open FleetMinder to get started.";
const expiredInvite = "This invite has expired. Ask for a new one below.";
const brokenInvite =
  "This invite link doesn't work. Ask the person who invited you to send a new one.";
const withdrawnInvite =
  "This invite was withdrawn. Ask the person who invited you if you should still join.";

export function getInvitationStatusMessage(
  status: InvitationStatus | string,
): string {
  const messages: Record<string, string> = {
    accepted: alreadyJoined,
    expired: expiredInvite,
    invitation_already_accepted: alreadyJoined,
    invitation_expired: expiredInvite,
    invitation_not_found: brokenInvite,
    invitation_revoked: withdrawnInvite,
    invitation_wrong_email:
      "This invite is for a different email address. Sign out, then sign in with the address it was sent to.",
    not_found: brokenInvite,
    revoked: withdrawnInvite,
  };

  return messages[status] ?? brokenInvite;
}

export async function requestPasswordRecovery(
  email: string,
  fetcher: typeof fetch = fetch,
  location: Pick<Location, "origin"> = window.location,
): Promise<PasswordRecoveryRequestResult> {
  const env = loadBrowserEnvironment();
  const response = await fetcher(
    new URL("/api/v1/auth/request-password-reset", env.VITE_API_BASE_URL),
    {
      body: JSON.stringify({
        email,
        redirectTo: `${location.origin}/auth/reset-password`,
      }),
      credentials: "include",
      headers: { "content-type": "application/json" },
      method: "POST",
    },
  );

  if (response.status === 429) {
    return {
      kind: "failed",
      message: "Too many tries. Wait a minute, then try again.",
    };
  }

  if (!response.ok) {
    return {
      kind: "failed",
      message:
        "We can't send reset emails right now. Try again in a few minutes.",
    };
  }

  return { kind: "requested" };
}

export async function resetPassword(
  input: { readonly newPassword: string; readonly token: string },
  fetcher: typeof fetch = fetch,
): Promise<PasswordResetResult> {
  const env = loadBrowserEnvironment();
  const response = await fetcher(
    new URL("/api/v1/auth/reset-password", env.VITE_API_BASE_URL),
    {
      body: JSON.stringify(input),
      credentials: "include",
      headers: { "content-type": "application/json" },
      method: "POST",
    },
  );

  if (response.ok) {
    return { kind: "reset" };
  }

  const errorCode = await readApiErrorCode(response);

  if (errorCode === "INVALID_TOKEN") {
    return {
      kind: "invalid_token",
      message:
        "This reset link has expired or was already used. Get a new one below.",
    };
  }

  return {
    kind: "failed",
    message: getProviderSafePasswordResetFailureMessage(response.status),
  };
}

export function getProviderSafeSignUpFailureMessage(status: number): string {
  if (status === 429) {
    return "Too many tries. Wait a minute, then try again.";
  }

  if (status >= 500) {
    return "We can't send emails right now, so we couldn't finish creating your account. Try again in a few minutes.";
  }

  return "We couldn't create your account. If you already have one, sign in instead.";
}

export function getProviderSafeVerificationFailureMessage(
  status: number,
): string {
  if (status === 429) {
    return "Too many tries. Wait a minute, then try again.";
  }

  if (status >= 500) {
    return "We can't send the email right now. Try again in a few minutes.";
  }

  return "We couldn't send the email. Check that your address is right and try again.";
}

export function getProviderSafeSignInFailureMessage(status: number): string {
  if (status === 429) {
    return "Too many tries. Wait a minute, then try again.";
  }

  if (status >= 500) {
    return "Sign-in is temporarily unavailable. Try again soon.";
  }

  return "That email and password don't match. Try again, or use Forgot password.";
}

/** Never render an OAuth provider's callback error to the person signing in. */
export function getGoogleSignInFailureMessage(): string {
  return "We couldn't connect to Google. Try again, or use your email and password.";
}

/** Connecting Google from the account page, where the person is already in. */
export function getGoogleAccountLinkFailureMessage(): string {
  return "We couldn't connect Google. Try again.";
}

export function getProviderSafePasswordResetFailureMessage(
  status: number,
): string {
  if (status === 429) {
    return "Too many tries. Wait a minute, then try again.";
  }

  if (status >= 500) {
    return "Password reset is temporarily unavailable. Try again soon.";
  }

  return "We couldn't save that password. Try a different one.";
}

export function getPasswordRecoveryConfirmationMessage(): string {
  return "Check your email. If there's an account for that address, the link is on its way.";
}

export function getPasswordResetLinkMessage(
  errorCode: string | null,
): string | undefined {
  if (errorCode === "INVALID_TOKEN") {
    return "This reset link has expired or was already used. Get a new one below.";
  }

  if (errorCode) {
    return "This reset link doesn't work. Get a new one below.";
  }

  return undefined;
}

export function getSafeReturnPath(
  rawReturnTo: string | null | undefined,
): string {
  if (
    rawReturnTo &&
    rawReturnTo.startsWith("/") &&
    !rawReturnTo.startsWith("//") &&
    !rawReturnTo.includes("\\") &&
    !rawReturnTo.startsWith("/auth/")
  ) {
    return rawReturnTo;
  }

  return "/app";
}

export function getPostSignInDestination(
  organizations: readonly OrganizationMembership[],
  returnTo: string,
): string {
  if (returnTo !== "/app") {
    return returnTo;
  }

  if (organizations.length === 0) {
    return "/app/onboarding/organization";
  }

  if (organizations.length === 1) {
    const [organization] = organizations;

    return organization
      ? `/app/organizations/${organization.organizationId}`
      : "/app/onboarding/organization";
  }

  return "/app/organizations/select";
}

export function createVerificationPendingPath(input: {
  readonly destination: string;
  readonly email: string;
}): string {
  const params = new URLSearchParams({
    destination: getSafeVerificationDestination(input.destination),
    email: input.email,
  });

  return `/auth/verify-email?${params.toString()}`;
}

export function getSafeVerificationDestination(
  rawDestination: string | null | undefined,
): string {
  if (
    rawDestination &&
    rawDestination.startsWith("/") &&
    !rawDestination.startsWith("//") &&
    !rawDestination.includes("\\")
  ) {
    return rawDestination;
  }

  return "/app/onboarding/organization";
}

export function getVerificationCallbackMessage(errorCode: string | null): {
  readonly actionLabel: string;
  readonly destination: string;
  readonly message: string;
  readonly title: string;
} {
  if (errorCode === "TOKEN_EXPIRED") {
    return {
      actionLabel: "Sign in",
      // The link carries no address, so signing in is what finds the
      // account and offers a fresh email.
      destination: "/auth/sign-in",
      message: "Sign in to get a new one.",
      title: "This link has expired",
    };
  }

  if (errorCode) {
    return {
      actionLabel: "Sign in",
      destination: "/auth/sign-in",
      message:
        "It may have been used already. Sign in to continue, or get a new link.",
      title: "That link didn't work",
    };
  }

  return {
    actionLabel: "Continue",
    destination: "/app/onboarding/organization",
    message: "Your email is confirmed.",
    title: "You're all set",
  };
}

export function savePendingOrganization(input: {
  readonly companyName: string;
  readonly email: string;
}): void {
  window.localStorage.setItem(
    pendingOrganizationStorageKey,
    JSON.stringify({
      companyName: input.companyName,
      email: input.email,
      savedAt: new Date().toISOString(),
    }),
  );
}

export function readPendingOrganization():
  | {
      readonly companyName: string;
      readonly email: string;
      readonly savedAt: string;
    }
  | undefined {
  const raw = window.localStorage.getItem(pendingOrganizationStorageKey);

  if (!raw) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<{
      readonly companyName: unknown;
      readonly email: unknown;
      readonly savedAt: unknown;
    }>;

    if (
      typeof parsed.companyName === "string" &&
      typeof parsed.email === "string" &&
      typeof parsed.savedAt === "string"
    ) {
      return {
        companyName: parsed.companyName,
        email: parsed.email,
        savedAt: parsed.savedAt,
      };
    }
  } catch {
    window.localStorage.removeItem(pendingOrganizationStorageKey);
  }

  return undefined;
}

function createVerificationCallbackUrl(
  origin: string,
  destination: string,
): string {
  const params = new URLSearchParams({
    destination: getSafeVerificationDestination(destination),
  });

  return `${origin}/auth/verification-complete?${params.toString()}`;
}

async function readApiErrorCode(
  response: Response,
): Promise<string | undefined> {
  const body = (await response.json().catch(() => undefined)) as
    | {
        readonly error?: {
          readonly code?: unknown;
        };
      }
    | undefined;

  return typeof body?.error?.code === "string" ? body.error.code : undefined;
}
